/**
 * Management of bookings.
 *
 * Deliberately the same reading as the public booking range — search, filter,
 * one card per building, opened one at a time — so that an operator and a
 * member of staff are looking at the same map of the deployment. What changes
 * is what a card contains: not free slots to take, but booked slots to follow,
 * cancel, and move along.
 *
 * Reaching "terminée" is what counts a workstation as delivered, so this
 * screen writes to the same place the technicians' one does. There is no
 * separate tally kept here.
 */
import { useMemo, useState } from "react";
import {
  FULFILLMENT_LABELS,
  FULFILLMENT_STATES,
  formatDayFr,
  type AdminSiteSummary,
  type FulfillmentState,
  type ReservationView,
} from "@soi/contracts";
import {
  useAdminCancelReservation,
  useAdminReservations,
  useAdminSendReminder,
  useAdminSites,
  useSetFulfillment,
  useZones,
} from "../lib/queries.js";
import { ApiFailure } from "../lib/api.js";
import { AddSlotsDialog } from "../components/AddSlotsDialog.js";

/** Short badge text; the select carries the full wording. */
const SHORT: Record<FulfillmentState, string> = {
  reservee: "Réservée",
  preparation: "Préparation",
  livraison: "Livraison",
  terminee: "Terminée",
};

function Row({
  reservation,
  siteSlug,
}: {
  reservation: ReservationView;
  siteSlug: string;
}) {
  const setFulfillment = useSetFulfillment(siteSlug);
  const cancel = useAdminCancelReservation(siteSlug);
  const remind = useAdminSendReminder();
  const [confirming, setConfirming] = useState(false);

  const busy = setFulfillment.isPending || cancel.isPending || remind.isPending;
  const failure = setFulfillment.error ?? cancel.error ?? remind.error;
  const cancelled = reservation.status === "cancelled";

  return (
    <div className={`resa${cancelled ? " off" : ""}`} data-testid={`resa-${reservation.id}`}>
      <div className="resa-when">
        <b>{reservation.time}</b>
        <span>{formatDayFr(reservation.day)}</span>
      </div>

      <div className="resa-who">
        <b>
          {reservation.firstName} {reservation.lastName}
        </b>
        <span>{reservation.email}</span>
        {reservation.floor && <span className="resa-floor">{reservation.floor}</span>}
      </div>

      <div className="resa-iv">{reservation.intervenantName}</div>

      {cancelled ? (
        <div className="resa-state">
          <span className="etat off">Annulée</span>
        </div>
      ) : (
        <>
          <div className="resa-state">
            <span className={`etat ${reservation.fulfillment}`}>
              {SHORT[reservation.fulfillment]}
            </span>
            <select
              aria-label={`Avancement — ${reservation.firstName} ${reservation.lastName}`}
              data-testid={`etat-${reservation.id}`}
              value={reservation.fulfillment}
              disabled={busy}
              onChange={(e) =>
                setFulfillment.mutate({
                  id: reservation.id,
                  fulfillment: e.target.value as FulfillmentState,
                })
              }
            >
              {FULFILLMENT_STATES.map((state) => (
                <option key={state} value={state}>
                  {FULFILLMENT_LABELS[state]}
                </option>
              ))}
            </select>
          </div>

          <div className="resa-act">
            {confirming ? (
              <>
                {/* Cancelling frees the slot for someone else and withdraws the
                    Outlook invitation: worth one deliberate second. */}
                <button
                  type="button"
                  className="lien-danger"
                  data-testid={`annuler-oui-${reservation.id}`}
                  disabled={busy}
                  onClick={() => cancel.mutate(reservation.id)}
                >
                  Confirmer
                </button>
                <button type="button" className="lien" onClick={() => setConfirming(false)}>
                  Non
                </button>
              </>
            ) : (
              <>
                {/* Disabled once sent, so a double click cannot mail the
                    person twice. Reloading the screen allows another. */}
                <button
                  type="button"
                  className="lien"
                  data-testid={`rappel-${reservation.id}`}
                  disabled={busy || remind.isSuccess}
                  onClick={() => remind.mutate(reservation.id)}
                >
                  {remind.isSuccess ? "Rappel en cours d'envoi" : "Envoyer un rappel"}
                </button>
                <button
                  type="button"
                  className="lien-danger"
                  data-testid={`annuler-${reservation.id}`}
                  disabled={busy}
                  onClick={() => setConfirming(true)}
                >
                  Annuler
                </button>
              </>
            )}
          </div>
        </>
      )}

      {failure && (
        <p className="resa-err" role="alert">
          {failure instanceof ApiFailure
            ? failure.message
            : "L'enregistrement a échoué. Réessayez."}
        </p>
      )}
    </div>
  );
}

function SiteCard({ site }: { site: AdminSiteSummary }) {
  const [open, setOpen] = useState(false);
  // Same restraint as the public page: a building's bookings are fetched only
  // once someone asks to see them.
  const { data: reservations, isLoading } = useAdminReservations(open ? site.slug : null);

  return (
    <article className="card" data-testid={`gestion-${site.slug}`}>
      <div className="head">
        <h2 className="cname">
          {site.name}
          {site.lot === "Police" ? (
            <span className="tag pol">Corps de Police</span>
          ) : (
            <span className="tag soi">{site.zone}</span>
          )}
        </h2>
        <p className="cmeta">
          Du <span className="daterange">{formatDayFr(site.firstDay)}</span> au{" "}
          <span className="daterange">{formatDayFr(site.lastDay)}</span>
          {" · "}
          <span data-testid={`resas-${site.slug}`}>{site.reservations}</span> réservation
          {site.reservations > 1 ? "s" : ""}
          {" · "}
          <span data-testid={`faites-${site.slug}`}>{site.done}</span> terminée
          {site.done > 1 ? "s" : ""}
        </p>
      </div>

      <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary>
          <span>Suivre les réservations</span>
        </summary>

        {isLoading && <p className="jour">Chargement des réservations…</p>}

        {reservations?.length === 0 && (
          <p className="jour">Aucune réservation pour ce bâtiment.</p>
        )}

        {reservations?.map((r) => (
          <Row key={r.id} reservation={r} siteSlug={site.slug} />
        ))}
      </details>
    </article>
  );
}

export function Reservations() {
  const { data: sites, isLoading, isError } = useAdminSites();
  const { data: zones } = useZones();
  const [search, setSearch] = useState("");
  const [zone, setZone] = useState("");
  const [addingSlots, setAddingSlots] = useState(false);

  const filtered = useMemo(() => {
    if (!sites) return [];
    const needle = search.trim().toLowerCase();
    return sites.filter(
      (s) =>
        (!needle || `${s.name} ${s.zone}`.toLowerCase().includes(needle)) &&
        (!zone || s.zone === zone),
    );
  }, [sites, search, zone]);

  const totals = useMemo(
    () =>
      (sites ?? []).reduce(
        (acc, s) => ({ resas: acc.resas + s.reservations, done: acc.done + s.done }),
        { resas: 0, done: 0 },
      ),
    [sites],
  );

  return (
    <div className="gestion">
      <div className="admin-main">
        <div className="wrap">
          <section className="panel">
            <h1>Suivi des réservations</h1>
            <p className="lede">
              L'avancement de chaque rendez-vous, bâtiment par bâtiment, dans la même lecture que
              la plage de réservation.
            </p>

            <div className="note">
              <b>{FULFILLMENT_LABELS.terminee} compte le poste comme livré.</b> C'est la même
              écriture que la validation d'un intervenant depuis le terrain, jamais un second
              compteur : les indicateurs et les stocks bougent avec. Revenir en arrière retire ce
              que cet écran a compté, mais laisse intacte une validation faite sur place.
            </div>

            <div className="resa-tools">
              <input
                type="search"
                placeholder="Rechercher un bâtiment ou une rue…"
                aria-label="Rechercher"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                aria-label="Filtrer par lieu"
                value={zone}
                onChange={(e) => setZone(e.target.value)}
              >
                <option value="">Tous les lieux</option>
                {zones?.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
              <span className="count" data-testid="gestion-count">
                {filtered.length} bâtiment{filtered.length > 1 ? "s" : ""} · {totals.done} /{" "}
                {totals.resas} terminée{totals.done > 1 ? "s" : ""}
              </span>
              <button type="button" className="btn small" onClick={() => setAddingSlots(true)}>
                + Ajouter des créneaux
              </button>
            </div>

            {isLoading && <p className="empty">Chargement…</p>}
            {isError && <p className="empty">Le service est momentanément indisponible.</p>}

            {!isLoading && !isError && filtered.length === 0 && (
              <div className="empty">
                {sites?.length === 0
                  ? "Aucune réservation n'a encore été prise."
                  : "Aucun bâtiment ne correspond."}
              </div>
            )}

            <div className="grid">
              {filtered.map((site) => (
                <SiteCard key={site.slug} site={site} />
              ))}
            </div>
          </section>
        </div>
      </div>
      {addingSlots && <AddSlotsDialog onClose={() => setAddingSlots(false)} />}
    </div>
  );
}
